
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/forms/submit/contact-us', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: '',
          position: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      title: '公司地址',
      content: '云南省西双版纳州景洪市工业园区茶叶大道88号',
      icon: 'ri-map-pin-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: '联系电话',
      content: '总机：0691-2345678\n销售热线：400-888-9999',
      icon: 'ri-phone-line',
      color: 'from-blue-400 to-blue-500'
    },
    {
      title: '电子邮箱',
      content: 'info@nandiangu.com\nsales@nandiangu.com',
      icon: 'ri-mail-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: '工作时间',
      content: '周一至周五：8:30-17:30\n周六：9:00-16:00',
      icon: 'ri-time-line',
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const offices = [
    {
      city: '昆明办事处',
      address: '云南省昆明市五华区春城路100号',
      phone: '0871-1234567',
      manager: '刘经理'
    },
    {
      city: '上海办事处',
      address: '上海市黄浦区南京东路200号',
      phone: '021-1234567',
      manager: '王经理'
    },
    {
      city: '广州办事处',
      address: '广东省广州市天河区天河路300号',
      phone: '020-1234567',
      manager: '李经理'
    },
    {
      city: '北京办事处',
      address: '北京市朝阳区建国门外大街400号',
      phone: '010-1234567',
      manager: '张经理'
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            联系我们
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            我们期待与您建立合作关系，共同推动茶叶行业的发展
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">联系信息</h3>
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex items-start p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${info.color} flex items-center justify-center mr-4 flex-shrink-0`}>
                    <i className={`${info.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">{info.title}</h4>
                    <p className="text-gray-600 whitespace-pre-line">{info.content}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 bg-blue-600 text-white">
                <h4 className="font-bold flex items-center">
                  <i className="ri-map-line mr-2"></i>
                  公司位置
                </h4>
              </div>
              <div className="aspect-video">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3580.123456789!2d100.8!3d22.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDAwJzAwLjAiTiAxMDDCsDQ4JzAwLjAiRQ!5e0!3m2!1szh-CN!2scn!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="公司位置"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">留言咨询</h3>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-us">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="请输入您的姓名"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      电话 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="请输入您的联系电话"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      邮箱
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      公司名称
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="请输入您的公司名称"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      职位
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="请输入您的职位"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      咨询主题 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white cursor-pointer"
                      required
                    >
                      <option value="">请选择咨询主题</option>
                      <option value="产品咨询">产品咨询</option>
                      <option value="合作洽谈">合作洽谈</option>
                      <option value="售后服务">售后服务</option>
                      <option value="招聘咨询">招聘咨询</option>
                      <option value="其他咨询">其他咨询</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-gray-700 font-medium mb-2">
                    留言内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    rows={4}
                    placeholder="请详细描述您的咨询内容（限500字符）"
                    required
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        发送留言
                      </>
                    )}
                  </button>
                </div>

                {/* Submit Status */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center text-green-800">
                      <i className="ri-check-circle-line text-xl mr-2"></i>
                      <span className="font-medium">留言发送成功！</span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      感谢您的留言，我们会在24小时内回复您。
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center text-red-800">
                      <i className="ri-error-warning-line text-xl mr-2"></i>
                      <span className="font-medium">发送失败</span>
                    </div>
                    <p className="text-red-700 text-sm mt-1">
                      网络异常，请稍后重试或直接拨打我们的联系电话。
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Branch Offices */}
        <div>
          <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">分支机构</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offices.map((office, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group hover:transform hover:scale-105"
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-building-line text-blue-600"></i>
                  </div>
                  <h4 className="font-bold text-gray-800">{office.city}</h4>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line mr-2 mt-1 text-gray-400"></i>
                    <span>{office.address}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-phone-line mr-2 text-gray-400"></i>
                    <span>{office.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2 text-gray-400"></i>
                    <span>负责人：{office.manager}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">快速链接</h3>
          <p className="text-blue-100 mb-6">
            了解更多关于南滇古茶的信息，或开始您的合作之旅
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/brand-center"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-award-line text-2xl mb-2"></i>
              <div className="font-semibold">品牌中心</div>
              <div className="text-blue-100 text-sm">了解我们的品牌故事</div>
            </a>
            <a
              href="/anchor-cooperation"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-live-line text-2xl mb-2"></i>
              <div className="font-semibold">主播合作</div>
              <div className="text-blue-100 text-sm">加入我们的合作计划</div>
            </a>
            <a
              href="/factory-cooperation"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-building-line text-2xl mb-2"></i>
              <div className="font-semibold">工厂合作</div>
              <div className="text-blue-100 text-sm">与我们建立合作关系</div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
